import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getTermsOverride } from '../providers/embedConfig'
import { VoucherStrategy, useAuthStore } from '../store/auth'
import { bundledDictionaries } from './dictionaries'
import { loadDictionary } from './loadDictionary'
import { SUPPORTED_LOCALES, type Dict, type SupportedLocale, type TranslationValues } from './types'

/**
 * Localization context. The active locale is seeded from the session (`initialLocale`, from Core's
 * `locale`) and can be overridden in-session (language switcher). Translations are looked up by a
 * dotted path — `t('items.selectTitle', { count })` — and interpolated with `{placeholder}` values.
 * The dictionary starts from the bundled copy (no flash) and is (re)loaded via `loadDictionary`,
 * the seam for fetching from an endpoint later.
 *
 * `availableLocales` is what a language switcher may offer: the SHOP's languages, not every language
 * we bundle. Read it rather than SUPPORTED_LOCALES anywhere the customer picks a language.
 */
interface LocaleContextValue {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
  availableLocales: SupportedLocale[]
  t: (path: string, vars?: TranslationValues) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function interpolate(text: string, vars?: TranslationValues): string {
  if (!vars) return text
  // `{key}` interpolates the value; `{key|cap}` capitalises its first letter, so a lowercase subject
  // token (e.g. {noun} = "clothing") can start a sentence as "Clothing" without a second token.
  return text.replace(/\{([^}|]+)(\|cap)?\}/g, (_, key, cap) => {
    if (vars[key] === undefined) return `{${key}}`
    const v = String(vars[key])
    return cap ? v.charAt(0).toUpperCase() + v.slice(1) : v
  })
}

/** A leaf whose wording varies by voucher strategy: `{ received, handin, instant }` (see StrategyText). */
function isStrategyText(v: unknown): v is Record<'received' | 'handin' | 'instant', string> {
  return typeof v === 'object' && v !== null && typeof (v as { received?: unknown }).received === 'string'
}

function translate(dict: Dict, path: string, strategyKey: string, vars?: TranslationValues): string {
  let current: unknown = dict
  for (const key of path.split('.')) {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key]
    } else {
      return path // surface missing keys instead of throwing
    }
  }
  // Resolve a strategy-variant leaf to the active strategy's string (received = default/fallback).
  if (isStrategyText(current)) {
    current = (current as Record<string, string>)[strategyKey] ?? current.received
  }
  return typeof current === 'string' ? interpolate(current, vars) : path
}

export function LocaleProvider({
  initialLocale,
  shopLocales,
  children,
}: {
  initialLocale: SupportedLocale
  /**
   * The shop's configured languages (Core's `ShopLanguages`, already narrowed to ones we bundle).
   * Omitted or empty — which is also the case while that query is still in flight — means "we don't
   * know yet", and the switcher falls back to offering everything we ship.
   */
  shopLocales?: readonly SupportedLocale[]
  children: ReactNode
}) {
  const [locale, setLocale] = useState<SupportedLocale>(initialLocale)
  const [dict, setDict] = useState<Dict>(() => bundledDictionaries[initialLocale])

  // Follow the session locale when it resolves/changes (unless the user has switched manually).
  useEffect(() => {
    setLocale(initialLocale)
  }, [initialLocale])

  // Load the dictionary for the active locale (bundled now; endpoint-ready via loadDictionary).
  useEffect(() => {
    let active = true
    setDict(bundledDictionaries[locale])
    loadDictionary(locale).then((d) => {
      if (active) setDict(d)
    })
    return () => {
      active = false
    }
  }, [locale])

  // The subject-word lexicon ({noun}/{nounPoss}/{nounDef}/{pieces}) made available to EVERY t() call
  // as interpolation vars: the dictionary's clothing defaults, overlaid with any brand override from
  // tradeInConfig.terms[locale]. Call-site vars still win over these (spread order below).
  const subject = useMemo<TranslationValues>(
    () => ({ ...(dict.subject as Record<string, string>), ...getTermsOverride(locale) }),
    [dict, locale],
  )

  // The brand's voucher strategy selects strategy-variant wording (default RECEIVED = today's copy).
  // Lowercased to match the StrategyText keys ({ received, handin, instant }).
  const strategy = useAuthStore((s) => s.config.voucherStrategy) ?? VoucherStrategy.Received
  const strategyKey = strategy.toLowerCase()

  // What the switcher offers. The active locale is always included: Core can seed a session locale
  // the shop no longer lists, and a switcher that doesn't contain its own value renders as unset.
  // Keyed on the joined list rather than the array, so a caller passing a freshly built array each
  // render (the normal shape — it's derived from a query result) doesn't churn the whole context.
  const shopLocaleKey = (shopLocales ?? []).join(',')
  const availableLocales = useMemo<SupportedLocale[]>(() => {
    const shop = shopLocaleKey ? (shopLocaleKey.split(',') as SupportedLocale[]) : []
    if (!shop.length) return SUPPORTED_LOCALES
    return SUPPORTED_LOCALES.filter((l) => shop.includes(l) || l === locale)
  }, [shopLocaleKey, locale])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      availableLocales,
      t: (path, vars) => translate(dict, path, strategyKey, { ...subject, ...vars }),
    }),
    [locale, dict, subject, strategyKey, availableLocales],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useTranslation must be used within a <LocaleProvider>')
  return ctx
}
